<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import FileIcon from 'dashboard/components-next/icon/FileIcon.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ProgressBar from 'dashboard/components-next/progress/ProgressBar.vue';
import Confetti from 'dashboard/components-next/confetti/Confetti.vue';
import SelectKnowledgeBaseDialog from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/SelectKnowledgeBaseDialog.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const knowledgeBaseId = computed(() => Number(route.params.id));
const knowledgeBase = ref(null);

// 知识库切换弹窗
const selectKbDialogRef = ref(null);

// 图片路径
import documentThumbnail from 'assets/images/dashboard/pilot/document-light.svg';
import documentThumbnailDark from 'assets/images/dashboard/pilot/document-dark.svg';

// 图片加载错误处理
const handleThumbnailError = event => {
  event.target.style.display = 'none';
};

// 文件上传模式状态
const selectedFiles = ref([]);
const fileInputRef = ref(null);
const isDragOver = ref(false);
const isSubmitting = ref(false);

// 每个文件的进度条 ref 数组
const progressBarRefs = ref([]);

// 文件状态管理
const FILE_STATUS = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  SUCCESS: 'success',
  ERROR: 'error',
};

const fileStatuses = ref({});

// 状态文字配置
const statusTexts = {
  uploading: '正在上传...',
  processing: '正在处理...',
  saving: '正在保存...',
  completing: '即将完成...',
};

// 每个文件的状态文字
const fileStatusTexts = ref({});

// 上传结果
const uploadResult = ref(null);
const showConfetti = ref(false);

// 假数据（模拟已上传文件）
const mockFiles = [
  { name: '产品手册.docx', size: '2.3 MB', type: 'docx' },
  { name: '常见问题解答.pdf', size: '1.5 MB', type: 'pdf' },
  { name: '解决方案.md', size: '0.8 MB', type: 'md' },
];

// 获取知识库详情
const fetchKnowledgeBase = async () => {
  try {
    const response = await store.dispatch('pilotKnowledgeBases/show', {
      id: knowledgeBaseId.value,
    });
    knowledgeBase.value = response.payload;
  } catch (error) {
    console.error('Failed to fetch knowledge base:', error);
  }
};

// 监听路由参数变化，重新获取知识库数据
watch(
  () => route.params.id,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      fetchKnowledgeBase();
    }
  }
);

// 打开知识库选择弹窗
const openSelectKbDialog = () => {
  selectKbDialogRef.value?.open();
};

// 切换知识库
const handleKnowledgeBaseChange = newKbId => {
  router.replace({
    name: 'pilot_documents_file_upload',
    params: { id: newKbId },
  });
};

// 文件上传相关函数
const openFileDialog = () => {
  fileInputRef.value?.click();
};

const handleFileSelect = files => {
  const MAX_FILE_SIZE = 80 * 1024 * 1024; // 80MB
  const MAX_FILES = 10;
  // 支持的文件类型（DOC不支持，PPT支持）
  const allowedExtensions = [
    'pdf',
    'txt',
    'md',
    'markdown',
    'docx',
    'ppt',
    'pptx',
  ];

  const fileArray = Array.from(files);
  if (fileArray.length === 0) return;

  const errors = [];
  const validFiles = [];
  const existingNames = new Set(selectedFiles.value.map(f => f.name));
  let hasDuplicate = false;

  // 1. 检查文件数量限制
  const currentCount = selectedFiles.value.length;
  const availableSlots = MAX_FILES - currentCount;

  if (availableSlots <= 0) {
    useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.FILE_LIMIT_ERROR'));
    return;
  }

  // 只取前N个可用的文件
  const filesToProcess = fileArray.slice(0, availableSlots);
  if (fileArray.length > availableSlots) {
    useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.FILE_LIMIT_ERROR'));
  }

  // 2. 遍历检查每个文件
  for (const file of filesToProcess) {
    const fileName = file.name.toLowerCase();
    const extension = fileName.split('.').pop();

    // 类型检查
    if (!allowedExtensions.includes(extension)) {
      errors.push(
        t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.FILE_TYPE_ERROR')
      );
      continue;
    }

    // 大小检查
    if (file.size > MAX_FILE_SIZE) {
      errors.push(
        t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.FILE_SIZE_ERROR')
      );
      continue;
    }

    // 重复检查（静默去重）
    if (existingNames.has(file.name)) {
      hasDuplicate = true;
      continue;
    }

    existingNames.add(file.name);
    validFiles.push(file);
  }

  // 3. 显示所有错误toast（去重）
  const uniqueErrors = [...new Set(errors)];
  uniqueErrors.forEach(err => useAlert(err));

  // 4. 如果有去重，提示
  if (hasDuplicate) {
    useAlert(
      t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.FILE_DUPLICATE_INFO')
    );
  }

  selectedFiles.value = [...selectedFiles.value, ...validFiles];
};

const handleFileChange = event => {
  const files = event.target.files;
  if (files.length === 0) return;
  handleFileSelect(files);
  event.target.value = ''; // 清空input，允许重复选择相同文件
};

const handleDragOver = event => {
  event.preventDefault();
  isDragOver.value = true;
};

const handleDragLeave = event => {
  event.preventDefault();
  isDragOver.value = false;
};

const handleDrop = event => {
  event.preventDefault();
  isDragOver.value = false;
  const files = event.dataTransfer.files;
  if (files.length === 0) return;
  handleFileSelect(files);
};

const removeFile = index => {
  const fileName = selectedFiles.value[index]?.name;
  selectedFiles.value.splice(index, 1);
  // 清除对应的状态
  if (fileName && fileStatuses.value[fileName]) {
    delete fileStatuses.value[fileName];
  }
};

// 格式化文件大小
const formatFileSize = bytes => {
  if (!bytes) return '';
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  const size = parseFloat((bytes / k ** i).toFixed(1));
  return `${size} ${sizes[i]}`;
};

// 从文件名获取扩展名（用于显示文件类型图标）
const getFileExtension = file => {
  if (!file.name) return null;

  const match = file.name.toLowerCase().match(/\.(\w+)$/);
  if (!match) return null;

  const ext = match[1];
  // 支持的扩展名列表
  const supportedExtensions = [
    'pdf',
    'txt',
    'md',
    'docx',
    'doc',
    'pptx',
    'ppt',
    'xlsx',
    'xls',
    'markdown',
  ];
  if (supportedExtensions.includes(ext)) {
    return ext;
  }

  // 默认返回 null（使用通用图标）
  return null;
};

// 获取文件状态
const getFileStatus = file => {
  return (
    fileStatuses.value[file.name] || {
      status: FILE_STATUS.PENDING,
      error: null,
    }
  );
};

// 获取文件状态文字
const getFileStatusText = file => {
  return fileStatusTexts.value[file.name] || statusTexts.uploading;
};

// 更新文件状态
const updateFileStatus = (fileName, status, error = null) => {
  fileStatuses.value[fileName] = { status, error };
};

// 更新状态文字
const updateStatusText = (fileName, progress) => {
  if (progress >= 90) {
    fileStatusTexts.value[fileName] = statusTexts.completing;
  } else if (progress < 30) {
    fileStatusTexts.value[fileName] = statusTexts.uploading;
  } else if (progress < 70) {
    fileStatusTexts.value[fileName] = statusTexts.processing;
  } else {
    fileStatusTexts.value[fileName] = statusTexts.saving;
  }
};

// 处理进度变化
const handleProgressUpdate = (fileName, progress) => {
  updateStatusText(fileName, progress);
};

// 返回列表页
const goBack = () => {
  router.push({
    name: 'pilot_knowledge_bases_documents',
    params: { id: knowledgeBaseId.value },
  });
};

// 提交处理
const handleSubmit = async () => {
  if (selectedFiles.value.length === 0) {
    useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_NO_FILES_ERROR'));
    return;
  }
  await submitFiles();
};

const submitFiles = async () => {
  isSubmitting.value = true;
  uploadResult.value = null;
  showConfetti.value = false;

  let successCount = 0;
  let errorCount = 0;

  for (let i = 0; i < selectedFiles.value.length; i++) {
    const file = selectedFiles.value[i];

    // 初始化状态
    updateFileStatus(file.name, FILE_STATUS.UPLOADING);

    // 启动进度条动画（使用组件的 start 方法）
    progressBarRefs.value[i]?.start();

    try {
      const formData = new FormData();
      formData.append('document[knowledge_base_id]', knowledgeBaseId.value);
      formData.append('document[pdf_file]', file);

      await store.dispatch('pilotDocuments/create', formData);

      // 等待进度条走完
      await progressBarRefs.value[i]?.complete();

      updateFileStatus(file.name, FILE_STATUS.SUCCESS);
      successCount++;
    } catch (error) {
      // 等待进度条走完
      await progressBarRefs.value[i]?.complete();

      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.UPLOAD_ERROR');
      updateFileStatus(file.name, FILE_STATUS.ERROR, errorMsg);
      errorCount++;
    }
  }

  isSubmitting.value = false;

  // 根据结果显示不同效果
  handleUploadComplete(successCount, errorCount);
};

// 上传完成处理
const handleUploadComplete = (successCount, errorCount) => {
  if (errorCount === 0) {
    // 全部成功
    showConfetti.value = true;
    uploadResult.value = {
      success: true,
      successCount,
      errorCount: 0,
    };
  } else if (successCount > 0) {
    // 部分成功
    showConfetti.value = true;
    uploadResult.value = {
      success: false,
      partialSuccess: true,
      successCount,
      errorCount,
    };
  } else {
    // 全部失败
    showConfetti.value = false;
    uploadResult.value = {
      success: false,
      partialSuccess: false,
      successCount: 0,
      errorCount,
    };
  }
};

// 确认按钮 - 跳转到列表页
const handleConfirm = () => {
  router.push({
    name: 'pilot_knowledge_bases_documents',
    params: { id: knowledgeBaseId.value },
  });
};

// 关闭弹窗
const handleCloseModal = () => {
  uploadResult.value = null;
};

// 离开确认
const hasUnsavedChanges = computed(() => isSubmitting.value);

const handleBeforeUnload = event => {
  if (hasUnsavedChanges.value) {
    event.preventDefault();
    event.returnValue = '';
    return '';
  }
};

onMounted(() => {
  fetchKnowledgeBase();
  window.addEventListener('beforeunload', handleBeforeUnload);
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload);
});

onBeforeRouteLeave((to, from, next) => {
  if (hasUnsavedChanges.value) {
    const confirmed = window.confirm(
      t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.LEAVE_CONFIRM')
    );
    if (confirmed) {
      next();
    } else {
      next(false);
    }
  } else {
    next();
  }
});
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-slate-white dark:bg-n-slate-800">
    <!-- 彩带庆祝效果 -->
    <Confetti :visible="showConfetti" />

    <!-- 成功弹窗 -->
    <Teleport to="body">
      <div v-if="uploadResult?.success" class="success-modal-overlay">
        <div class="success-modal-content">
          <div class="flex flex-col items-center gap-4">
            <div
              class="w-16 h-16 rounded-full bg-n-emerald-100 dark:bg-n-emerald-900/30 flex items-center justify-center"
            >
              <span
                class="i-lucide-check text-3xl text-n-emerald-600 dark:text-n-emerald-400"
              />
            </div>
            <h3 class="text-lg font-semibold text-n-slate-12">
              {{
                t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.SUCCESS_TITLE')
              }}
            </h3>
            <p class="text-sm text-n-slate-11 text-center">
              {{
                t(
                  'PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.SUCCESS_MESSAGE',
                  { count: uploadResult.successCount }
                )
              }}
            </p>
            <Button
              :label="
                t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.CONFIRM_BUTTON')
              "
              blue
              class="w-full"
              @click="handleConfirm"
            />
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 部分成功弹窗 -->
    <Teleport to="body">
      <div v-if="uploadResult?.partialSuccess" class="success-modal-overlay">
        <div class="success-modal-content">
          <div class="flex flex-col items-center gap-4">
            <div
              class="w-16 h-16 rounded-full bg-n-amber-100 dark:bg-n-amber-900/30 flex items-center justify-center"
            >
              <span
                class="i-lucide-alert-triangle text-3xl text-n-amber-600 dark:text-n-amber-400"
              />
            </div>
            <h3 class="text-lg font-semibold text-n-slate-12">
              {{
                t(
                  'PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.PARTIAL_SUCCESS_TITLE'
                )
              }}
            </h3>
            <p class="text-sm text-n-slate-11 text-center">
              {{
                t(
                  'PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.PARTIAL_SUCCESS_MESSAGE',
                  {
                    successCount: uploadResult.successCount,
                    errorCount: uploadResult.errorCount,
                  }
                )
              }}
            </p>
            <div class="flex gap-3 w-full">
              <Button
                :label="
                  t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.CLOSE_BUTTON')
                "
                variant="faded"
                color="slate"
                class="flex-1"
                @click="handleCloseModal"
              />
              <Button
                :label="
                  t(
                    'PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.CONFIRM_BUTTON'
                  )
                "
                blue
                class="flex-1"
                @click="handleConfirm"
              />
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 全部失败弹窗 -->
    <Teleport to="body">
      <div
        v-if="uploadResult?.success === false && !uploadResult?.partialSuccess"
        class="success-modal-overlay"
      >
        <div class="success-modal-content">
          <div class="flex flex-col items-center gap-4">
            <div
              class="w-16 h-16 rounded-full bg-n-red-100 dark:bg-n-red-900/30 flex items-center justify-center"
            >
              <span
                class="i-lucide-x text-3xl text-n-red-600 dark:text-n-red-400"
              />
            </div>
            <h3 class="text-lg font-semibold text-n-slate-12">
              {{ t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.ERROR_TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11 text-center">
              {{
                t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.ERROR_MESSAGE', {
                  count: uploadResult.errorCount,
                })
              }}
            </p>
            <Button
              :label="
                t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.CLOSE_BUTTON')
              "
              variant="faded"
              color="slate"
              class="w-full"
              @click="handleCloseModal"
            />
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Header -->
    <header class="sticky top-0 z-10 px-6 bg-n-slate-white dark:bg-n-slate-800">
      <div class="w-full mx-auto max-w-[60rem]">
        <div class="flex items-center h-20">
          <button
            type="button"
            class="p-2 -ml-2 text-n-slate-600 dark:text-n-slate-400 hover:bg-n-slate-100 dark:hover:bg-n-slate-700 rounded-lg transition-colors"
            @click="goBack"
          >
            <Icon icon="i-lucide-arrow-left" class="h-5 w-5" />
          </button>
          <h1
            class="ml-4 text-lg font-semibold text-n-slate-900 dark:text-n-slate-100"
          >
            {{ t('PILOT.KNOWLEDGE_BASES.SIDEBAR.UPLOAD_DOCUMENT') }}
          </h1>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="flex-1 w-full px-6 overflow-y-auto">
      <div class="w-full mx-auto max-w-[60rem] py-6 flex flex-col gap-6">
        <!-- 虚线介绍区域 -->
        <section class="custom-dashed-border rounded-2xl py-5 px-6">
          <div
            class="flex flex-col md:flex-row items-start md:items-center gap-6"
          >
            <div
              class="flex-shrink-0 w-[7.5rem] h-[6.5rem] rounded-lg flex items-center justify-center overflow-hidden"
            >
              <img
                :src="documentThumbnail"
                :alt="
                  t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.GUIDE_TITLE')
                "
                class="w-full h-full object-cover rounded-lg block dark:hidden"
                @error="handleThumbnailError"
              />
              <img
                :src="documentThumbnailDark"
                :alt="
                  t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.GUIDE_TITLE')
                "
                class="w-full h-full object-cover rounded-lg hidden dark:block"
                @error="handleThumbnailError"
              />
            </div>
            <div class="flex flex-col flex-1 gap-3">
              <p class="text-n-slate-12 text-sm font-medium">
                {{
                  t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.GUIDE_TITLE')
                }}
              </p>
              <p class="text-n-slate-11 text-sm">
                {{
                  t(
                    'PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.GUIDE_DESCRIPTION'
                  )
                }}
              </p>
            </div>
          </div>
        </section>

        <!-- 知识库信息（锁定状态） -->
        <div
          class="w-full flex items-start justify-between gap-3 p-4 rounded-xl outline-1 outline outline-n-container bg-n-solid-2 text-left"
        >
          <!-- Logo -->
          <div
            class="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden"
            :class="knowledgeBase?.logo_url ? '' : 'bg-n-brand/10'"
          >
            <img
              v-if="knowledgeBase?.logo_url"
              :src="knowledgeBase.logo_url"
              :alt="knowledgeBase?.name"
              class="w-full h-full object-cover"
            />
            <span v-else class="text-base font-semibold text-n-brand">
              {{ knowledgeBase?.name?.charAt(0)?.toUpperCase() || '?' }}
            </span>
          </div>

          <!-- 信息 -->
          <div class="flex flex-col min-w-0 flex-1">
            <div class="flex items-center gap-2">
              <span class="text-sm text-n-slate-12 font-medium">{{
                knowledgeBase?.name || '加载中...'
              }}</span>
              <span
                v-if="knowledgeBase?.knowledge_base_type === 'faq'"
                class="px-2 py-0.5 text-xs rounded-full font-medium bg-n-emerald-100 text-n-emerald-700 dark:bg-n-emerald-900/30 dark:text-n-emerald-400"
              >
                FAQs
              </span>
            </div>
            <span class="text-sm text-n-slate-11 mt-2">
              {{
                knowledgeBase?.description ||
                t('PILOT.KNOWLEDGE_BASES.SELECT_KB_DIALOG.NO_DESCRIPTION')
              }}
            </span>
            <!-- 文档和FAQ数量 -->
            <div class="flex items-center gap-4 text-sm text-n-slate-11 mt-2">
              <div class="flex items-center gap-1.5">
                <span class="i-lucide-file-text size-3" />
                <span>{{ knowledgeBase?.documents_count || 0 }} 文档</span>
              </div>
              <div class="flex items-center gap-1.5">
                <span class="i-lucide-message-circle-question size-3" />
                <span>{{ knowledgeBase?.faqs_count || 0 }} FAQ</span>
              </div>
            </div>
          </div>

          <!-- 切换按钮 -->
          <button
            type="button"
            class="flex items-center gap-1 text-sm text-n-slate-9 hover:text-n-brand flex-shrink-0 transition-colors"
            @click="openSelectKbDialog"
          >
            <span class="i-lucide-arrow-right-left size-4" />
            <span>{{ t('PILOT.KNOWLEDGE_BASES.SWITCH_KB') }}</span>
          </button>
        </div>

        <!-- 拖拽上传区域 -->
        <div
          class="relative flex flex-col items-center justify-center h-64 p-6 border-2 border-dashed rounded-lg transition-colors"
          :class="[
            isSubmitting ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            isDragOver
              ? 'border-n-blue-5 bg-n-blue-1 dark:bg-n-blue-2'
              : 'border-n-slate-3 hover:border-n-slate-4',
          ]"
          @dragover="isSubmitting ? null : handleDragOver($event)"
          @dragleave="isSubmitting ? null : handleDragLeave($event)"
          @drop="isSubmitting ? null : handleDrop($event)"
          @click="isSubmitting ? null : openFileDialog()"
        >
          <input
            ref="fileInputRef"
            type="file"
            accept=".pdf,.txt,.md,.markdown,.docx,.ppt,.pptx"
            multiple
            class="hidden"
            @change="handleFileChange"
          />
          <div class="i-lucide-upload-cloud text-5xl text-n-slate-7 mb-4" />
          <p class="text-base font-medium text-n-slate-12 mb-2">
            {{ t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.DRAG_DROP_TITLE') }}
          </p>
          <p class="text-sm text-n-slate-11">
            {{ t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.DRAG_DROP_HELP') }}
          </p>
        </div>

        <!-- 已选文件列表 -->
        <div v-if="selectedFiles.length > 0" class="flex flex-col gap-3">
          <div class="flex items-center justify-between">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{
                t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.SELECTED_FILES', {
                  count: selectedFiles.length,
                })
              }}
            </h3>
          </div>

          <div class="flex flex-col gap-2">
            <div
              v-for="(file, index) in selectedFiles"
              :key="index"
              class="flex items-start justify-between p-3 bg-n-slate-1 dark:bg-n-slate-7 rounded-lg group"
            >
              <div class="flex items-start gap-3 flex-1 min-w-0">
                <FileIcon
                  v-if="getFileExtension(file)"
                  :file-type="getFileExtension(file)"
                  class="text-2xl text-n-slate-7 flex-shrink-0 mt-0.5"
                />
                <div
                  v-else
                  class="i-lucide-file text-2xl text-n-slate-7 flex-shrink-0 mt-0.5"
                />
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <p class="text-sm font-medium text-n-slate-12 truncate">
                      {{ file.name }}
                    </p>
                    <!-- 状态图标 -->
                    <span
                      v-if="getFileStatus(file).status === FILE_STATUS.SUCCESS"
                      class="i-lucide-check text-green-500 flex-shrink-0"
                    />
                    <span
                      v-else-if="
                        getFileStatus(file).status === FILE_STATUS.ERROR
                      "
                      class="i-lucide-x text-n-red-500 flex-shrink-0"
                    />
                  </div>
                  <p class="text-xs text-n-slate-11">
                    {{ formatFileSize(file.size) }}
                  </p>

                  <!-- 进度条组件 -->
                  <ProgressBar
                    :ref="el => (progressBarRefs[index] = el)"
                    :visible="
                      getFileStatus(file).status === FILE_STATUS.UPLOADING
                    "
                    :status-text="getFileStatusText(file)"
                    :show-success="false"
                    @update:progress="handleProgressUpdate(file.name, $event)"
                  />

                  <!-- 错误信息 -->
                  <p
                    v-if="getFileStatus(file).error"
                    class="text-xs text-n-red-11 mt-2 flex items-center gap-1.5"
                  >
                    <span class="i-lucide-alert-circle text-sm" />
                    {{ getFileStatus(file).error }}
                  </p>
                </div>
              </div>
              <Button
                icon="i-lucide-trash-2"
                xs
                ruby
                faded
                :disabled="isSubmitting"
                class="ml-3 flex-shrink-0"
                @click="removeFile(index)"
              />
            </div>
          </div>
        </div>

        <!-- 假数据预览区域（仅当没有选择文件时显示） -->
        <div
          v-if="selectedFiles.length === 0"
          class="relative overflow-hidden rounded-xl"
        >
          <!-- 假数据文件列表（半透明） -->
          <div class="w-full space-y-2 opacity-50 pointer-events-none">
            <div
              v-for="(mockFile, index) in mockFiles"
              :key="index"
              class="flex items-center p-3 bg-n-slate-1 dark:bg-n-slate-7 rounded-lg"
            >
              <div class="flex items-center gap-3 flex-1 min-w-0">
                <FileIcon
                  :file-type="mockFile.type"
                  class="text-2xl text-n-slate-7 flex-shrink-0"
                />
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-n-slate-12 truncate">
                    {{ mockFile.name }}
                  </p>
                  <p class="text-xs text-n-slate-11">
                    {{ mockFile.size }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- 渐变蒙层 + 标题/描述/按钮（从上往下渐变：透明 -> 白色） -->
          <div
            class="absolute inset-x-0 top-0 flex flex-col items-center justify-center w-full h-full bg-gradient-to-b from-transparent from-0% via-white/50 dark:via-n-slate-800/50 to-white dark:to-n-slate-800"
          >
            <div class="flex flex-col items-center justify-center gap-3">
              <h3 class="text-3xl font-medium text-center text-n-slate-12">
                {{
                  t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.PREVIEW_TITLE')
                }}
              </h3>
              <p class="max-w-xl text-base text-center text-n-slate-11">
                {{
                  t(
                    'PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.PREVIEW_DESCRIPTION'
                  )
                }}
              </p>
              <Button
                :label="
                  t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILE_UPLOAD.START_UPLOAD')
                "
                icon="i-lucide-upload-cloud"
                blue
                @click="openFileDialog"
              />
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer
      class="sticky bottom-0 z-10 px-6 py-4 bg-n-slate-white dark:bg-n-slate-800 border-t border-n-weak"
    >
      <div class="w-full mx-auto max-w-[60rem]">
        <div class="flex gap-3 justify-end">
          <Button
            :label="t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.CANCEL')"
            variant="faded"
            color="slate"
            :disabled="isSubmitting"
            @click="goBack"
          />
          <Button
            :label="
              t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.UPLOAD_FILES_BUTTON', {
                count: selectedFiles.length,
              })
            "
            :is-loading="isSubmitting"
            :disabled="isSubmitting || selectedFiles.length === 0"
            blue
            @click="handleSubmit"
          />
        </div>
      </div>
    </footer>
  </div>

  <!-- 知识库选择弹窗 -->
  <SelectKnowledgeBaseDialog
    ref="selectKbDialogRef"
    action-type="switch"
    @select="handleKnowledgeBaseChange"
  />
</template>

<style scoped>
/* 成功弹窗样式 */
.success-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
}

.success-modal-content {
  background-color: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  max-width: 28rem;
  margin: 1rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.dark .success-modal-content {
  background-color: #1e293b;
}
</style>
